import type * as THREE from 'three/webgpu';
import type {CollectSource} from '../../mesh/sketch/ISketchObject';
import type {SketchPointObject} from '../../mesh/sketch/SketchPointObject';

//------ 草图闭合模块化 开始------
/**
 * 草图闭合边遍历方向。
 *
 * 设计说明：
 * 1. 闭合轮廓搜索应把草图边视为无向拓扑边；
 * 2. 实际轮廓采样仍需要明确当前遍历方向；
 * 3. 使用枚举表达有限方向，避免在闭合模块内部散落魔法字符串。
 */
export enum SketchClosureTraversalDirection {
    /** 按边对象自身起点到终点遍历。 */
    Forward = 'forward',
    /** 按边对象自身终点到起点遍历。 */
    Reversed = 'reversed',
}

/**
 * 草图闭合拓扑顶点匹配来源。
 *
 * 设计说明：
 * 1. SharedPoint 表示两个端点复用同一个控制点对象，是最可靠的拓扑连接；
 * 2. Coordinate 表示不同端点对象在草图平面坐标容差内重合，用于修剪点、象限点等运行时新建端点的收口；
 * 3. 区分来源便于后续诊断闭合失败原因。
 */
export enum SketchClosureVertexMatchKind {
    /** 新建独立拓扑顶点。 */
    Created = 'created',
    /** 通过共享端点对象判定为同一拓扑顶点。 */
    SharedPoint = 'sharedPoint',
    /** 通过草图平面坐标容差判定为同一拓扑顶点。 */
    Coordinate = 'coordinate',
}

/**
 * 集合源闭合遍历步骤。
 *
 * 说明：
 * 1. `source` 为参与闭合链路的原始边对象；
 * 2. `reversed` 为 true 时表示该边按“终点 -> 起点”方向参与遍历；
 * 3. 该结构只描述闭合遍历方向，不修改边对象自身的端点定义。
 */
export interface SketchClosureTraversalStep {
    /** 当前遍历边对象。 */
    source: CollectSource;
    /** 是否按反向参与闭合遍历。 */
    reversed: boolean;
}

/**
 * 草图边端点。
 */
export interface SketchClosureSourceEndpoints {
    /** 起点控制点。 */
    startPoint: SketchPointObject;
    /** 终点控制点。 */
    endPoint: SketchPointObject;
}

/**
 * 草图闭合拓扑顶点。
 */
export interface SketchClosureTopologyVertex {
    /** 拓扑顶点键。 */
    key: string;
    /** 顶点代表控制点。 */
    point: SketchPointObject;
    /** 世界坐标位置。 */
    worldPosition: THREE.Vector3;
    /** 草图平面局部 2D 坐标。 */
    localPosition: THREE.Vector2;
    /** 已归并到该拓扑顶点的控制点 ID。 */
    pointIds: Set<string>;
    /** 顶点最初创建时的匹配来源。 */
    matchKind: SketchClosureVertexMatchKind;
}

/**
 * 草图闭合半边。
 */
export interface SketchClosureHalfEdge {
    /** 原始集合源边。 */
    source: CollectSource;
    /** 遍历步骤。 */
    traversalStep: SketchClosureTraversalStep;
    /** 起点拓扑键。 */
    startKey: string;
    /** 终点拓扑键。 */
    endKey: string;
}

/**
 * 草图闭合拓扑图。
 */
export interface SketchClosureTopologyGraph {
    /** 顶点表。 */
    vertices: Map<string, SketchClosureTopologyVertex>;
    /** 源边到半边列表的索引。 */
    halfEdgesBySourceId: Map<string, SketchClosureHalfEdge[]>;
}
//------ 草图闭合模块化 结束------
